import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Box,
  Text,
  Select,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import React, { useState, useCallback } from 'react' // Import useState and useCallback
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useCreateBlogPost } from 'queries/blogPost.query'
import Dropzone from '../../settings/components/Dropzone' // Assuming the correct path

// Updated Yup schema for Dropzone: cover_image validation simplified
const validationSchema = yup.object().shape({
  title: yup
    .string()
    .required('Title is required')
    .max(255, 'Title cannot be more than 255 characters'),
  excerpt: yup
    .string()
    .nullable()
    .max(500, 'Excerpt cannot be more than 500 characters'),
  content: yup.string().required('Content is required'),
  cover_image: yup
    .mixed()
    .nullable()
    .test(
      'fileType',
      'Unsupported file format (JPEG, PNG, GIF, SVG)',
      value => {
        // The value from Dropzone/setValue will be an array containing the File object, or null
        const file = value?.[0]
        if (!file) return true
        return [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'image/gif',
          'image/svg+xml',
        ].includes(file.type)
      },
    )
    .test('fileSize', 'File size is too large (max 2MB)', value => {
      const file = value?.[0]
      if (!file) return true
      return file.size <= 2048 * 1024
    }),
  author: yup
    .string()
    .nullable()
    .max(255, 'Author name cannot be more than 255 characters'),
  read_time: yup
    .string()
    .nullable()
    .max(50, 'Read time cannot be more than 50 characters'),
  date: yup.date().nullable().typeError('Invalid date format'),
  category: yup
    .string()
    .oneOf(['concert', 'cultural', 'sports'], 'Invalid category selected')
    .required('Category is required'),
})

export function CreateBlogPostModal ({ isOpen, onClose }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')
  const toast = useToast()

  // State for a temporary file preview URL (local blob URL)
  const [localFilePreview, setLocalFilePreview] = useState(null)

  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue, // Added setValue to manually set the file field
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      excerpt: '',
      content: '',
      // Ensure the initial value is null for the Dropzone logic
      cover_image: null,
      author: '',
      read_time: '',
      date: '',
      category: 'concert',
    },
  })

  const { mutate, isPending } = useCreateBlogPost()

  const handleFileChange = useCallback(
    file => {
      // 1. Clean up previous blob URL if exists
      if (localFilePreview) {
        URL.revokeObjectURL(localFilePreview)
      }

      if (file) {
        // 2. Set the file in react-hook-form state as an array [File]
        setValue('cover_image', [file], { shouldValidate: true })
        // 3. Set the new local preview URL
        setLocalFilePreview(URL.createObjectURL(file))
      } else {
        // 4. Clear the value and the preview
        setValue('cover_image', null, { shouldValidate: true })
        setLocalFilePreview(null)
      }
    },
    [localFilePreview, setValue],
  )

  /**
   * Submission handler
   */
  const onSubmit = formData => {
    const data = new FormData()

    for (const key in formData) {
      // Skip cover_image and date fields for initial append loop
      if (key === 'cover_image' || key === 'date') continue

      if (
        formData[key] !== null &&
        formData[key] !== undefined &&
        formData[key] !== ''
      ) {
        data.append(key, formData[key])
      }
    }

    // Append the cover image file correctly
    // formData.cover_image is an array [File] from setValue
    if (formData.cover_image && formData.cover_image.length > 0) {
      data.append('cover_image', formData.cover_image[0])
    }

    // Format and append date
    if (formData.date) {
      const date = new Date(formData.date)
      const formattedDate = date.toISOString().split('T')[0]
      data.append('date', formattedDate)
    } else {
      data.append('date', '')
    }

    data.append('status', 'DRAFT')

    mutate(data, {
      onSuccess: () => {
        // Cleanup local preview state after successful submission
        if (localFilePreview) {
          URL.revokeObjectURL(localFilePreview)
        }
        setLocalFilePreview(null)

        // Reset form
        reset()
        onClose()

        toast({
          title: 'Blog Post Created.',
          description: 'The new blog post has been successfully added.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        console.error('Create failed:', err)
        toast({
          title: 'Failed to Create Blog Post.',
          description:
            err.response?.data?.message ||
            'There was an error creating the blog post.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Blog Post</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box
            as='form'
            onSubmit={handleSubmit(onSubmit)}
            p={4}
            pointerEvents={isPending ? 'none' : 'auto'}
          >
            <VStack spacing={4}>
              <FormControl id='title' isInvalid={errors.title}>
                <FormLabel>Title</FormLabel>
                <Input
                  {...register('title')}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.title && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.title.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='excerpt' isInvalid={errors.excerpt}>
                <FormLabel>Excerpt</FormLabel>
                <Textarea
                  {...register('excerpt')}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.excerpt && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.excerpt.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='content' isInvalid={errors.content}>
                <FormLabel>Content</FormLabel>
                <Textarea
                  {...register('content')}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.content && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.content.message}
                  </Text>
                )}
              </FormControl>

              {/* Dropzone component replaces Input type='file' */}
              <FormControl id='cover_image' isInvalid={errors.cover_image}>
                <FormLabel>Cover Image</FormLabel>
                <Dropzone
                  title='Drag and drop an image here, or click to select'
                  onFileChange={handleFileChange}
                  preview={localFilePreview} // Pass the local preview URL
                />
                {errors.cover_image && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.cover_image.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='author' isInvalid={errors.author}>
                <FormLabel>Author</FormLabel>
                <Input
                  {...register('author')}
                  placeholder='John Doe'
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.author && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.author.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='read_time' isInvalid={errors.read_time}>
                <FormLabel>Read Time</FormLabel>
                <Input
                  {...register('read_time')}
                  placeholder='5 min read'
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.read_time && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.read_time.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='date' isInvalid={errors.date}>
                <FormLabel>Date</FormLabel>
                <Input
                  type='date'
                  {...register('date')}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                {errors.date && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.date.message}
                  </Text>
                )}
              </FormControl>

              <FormControl id='category' isInvalid={errors.category}>
                <FormLabel>Category</FormLabel>
                <Select {...register('category')}>
                  <option value='concert'>Concert</option>
                  <option value='cultural'>Cultural</option>
                  <option value='sports'>Sports</option>
                </Select>
                {errors.category && (
                  <Text color='red.500' fontSize='sm'>
                    {errors.category.message}
                  </Text>
                )}
              </FormControl>
            </VStack>
            <ModalFooter>
              <Button colorScheme='blue' type='submit' isLoading={isPending}>
                Create
              </Button>
              <Button colorScheme='gray' ml={3} onClick={onClose}>
                Cancel
              </Button>
            </ModalFooter>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
